import { create } from 'zustand';
import {
  BillingProductItem,
  BillingServiceType,
  BillingStatusResponse,
  PaymentRecordItem,
  ServiceEntitlementDto,
} from '@/types/billing';
import { PageResponse } from '@/common/types/common';
import { billingService } from '@/services/billingService';
import { toastUtils } from '@/common/utils/toastUtils';

interface BillingState {
  plans: BillingProductItem[];
  billingStatus: BillingStatusResponse | null;
  payments: PageResponse<PaymentRecordItem> | null;
  entitlement: ServiceEntitlementDto | null;
  isLoading: boolean;
  isPaymentsLoading: boolean;

  loadBillingData: (companyId: number, serviceType?: BillingServiceType) => Promise<void>;
  loadPayments: (companyId: number, page?: number, limit?: number) => Promise<void>;
  reset: () => void;
}

export const useBillingStore = create<BillingState>()((set) => ({
  plans: [],
  billingStatus: null,
  payments: null,
  entitlement: null,
  isLoading: false,
  isPaymentsLoading: false,

  loadBillingData: async (companyId: number, serviceType: BillingServiceType = 'INVESTOR') => {
    set({ isLoading: true });
    try {
      const [plans, billingStatus] = await Promise.all([
        billingService.getPlans(serviceType),
        billingService.getBillingStatus(companyId),
      ]);
      set({ plans, billingStatus });
    } catch (error) {
      console.error('결제 정보를 불러오는데 실패했습니다.', error);
      toastUtils.error('결제 정보를 불러오는데 실패했습니다.');
    } finally {
      set({ isLoading: false });
    }

    // 이용권(entitlement) 정보는 신규 API라 실패해도 기존 결제 화면 동작에 영향을 주지 않도록 별도 처리
    if (serviceType === 'INVESTOR') {
      try {
        const entitlement = await billingService.getEntitlement(companyId);
        set({ entitlement });
      } catch (error) {
        console.error('이용권 정보를 불러오는데 실패했습니다.', error);
        set({ entitlement: null });
      }
    } else {
      set({ entitlement: null });
    }
  },

  loadPayments: async (companyId: number, page: number = 1, limit: number = 10) => {
    set({ isPaymentsLoading: true });
    try {
      const payments = await billingService.getPayments(companyId, page, limit);
      set({ payments });
    } catch (error) {
      console.error('결제 내역을 불러오는데 실패했습니다.', error);
      toastUtils.error('결제 내역을 불러오는데 실패했습니다.');
    } finally {
      set({ isPaymentsLoading: false });
    }
  },

  reset: () => {
    set({
      plans: [],
      billingStatus: null,
      payments: null,
      entitlement: null,
      isLoading: false,
      isPaymentsLoading: false,
    });
  },
}));
